import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { api } from '../../lib/api';
import { SequenceStep, Template } from '../../types/campaign';
import { HelpIcon } from '../HelpIcon';
import { TooltipProvider } from '../ui/tooltip';
import { getHelpContent } from '../../lib/helpContent';

interface StepEditorProps {
  step?: SequenceStep;
  sequenceId: string;
  order: number;
  onSave: (stepData: Partial<SequenceStep>) => void;
  onCancel: () => void;
  isOpen: boolean;
}

export const StepEditor: React.FC<StepEditorProps> = ({
  step,
  sequenceId,
  order,
  onSave,
  onCancel,
  isOpen,
}) => {
  const [formData, setFormData] = useState<Partial<SequenceStep>>({
    name: '',
    step_type: 'email',
    template_id: '',
    delay_hours: undefined,
    delay_days: undefined,
    conditions: {},
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch templates
  const { data: templates = [] } = useQuery<Template[]>({
    queryKey: ['templates'],
    queryFn: async () => {
      const response = await api.templates.list();
      return Array.isArray(response.data) ? response.data : [];
    },
  });

  // Initialize form data when step changes
  useEffect(() => {
    if (step) {
      setFormData({
        name: step.name || '',
        step_type: step.step_type || 'email',
        template_id: step.template_id || '',
        delay_hours: step.delay_hours,
        delay_days: step.delay_days,
        conditions: step.conditions || {},
      });
    } else {
      setFormData({
        name: '',
        step_type: 'email',
        template_id: '',
        delay_hours: undefined,
        delay_days: undefined,
        conditions: {},
      });
    }
    setErrors({});
  }, [step, isOpen]);

  // Filter templates by step type
  const filteredTemplates = templates.filter((template) => {
    if (formData.step_type === 'email') {
      return template.template_type === 'email' && template.status === 'active';
    } else if (formData.step_type === 'sms') {
      return template.template_type === 'sms' && template.status === 'active';
    }
    return false;
  });

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name || formData.name.trim() === '') {
      newErrors.name = 'Step name is required';
    }

    if (formData.step_type === 'email' || formData.step_type === 'sms') {
      if (!formData.template_id || formData.template_id === '') {
        newErrors.template_id = 'Template is required for email/SMS steps';
      }
    }

    if (formData.step_type === 'delay') {
      if (!formData.delay_hours && !formData.delay_days) {
        newErrors.delay = 'At least one delay value (hours or days) is required';
      }
      if (formData.delay_hours !== undefined && (formData.delay_hours < 0 || formData.delay_hours > 23)) {
        newErrors.delay_hours = 'Delay hours must be between 0 and 23';
      }
      if (formData.delay_days !== undefined && formData.delay_days < 0) {
        newErrors.delay_days = 'Delay days must be 0 or greater';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSave({
        ...formData,
        order,
        sequence_id: sequenceId,
      });
    }
  };

  const handleStepTypeChange = (newType: string) => {
    setFormData({
      ...formData,
      step_type: newType as any,
      template_id: '', // Clear template when type changes
    });
    setErrors({});
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">
            {step ? 'Edit Step' : 'Add Step'}
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-500"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-6">
          {/* Step Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Step Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                errors.name ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="e.g., Welcome Email"
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>

          {/* Step Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Step Type <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.step_type}
              onChange={(e) => handleStepTypeChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="email">Email</option>
              <option value="sms">SMS</option>
              <option value="delay">Delay</option>
              <option value="condition">Condition</option>
            </select>
          </div>

          {/* Template Selection (for email/sms) */}
          {(formData.step_type === 'email' || formData.step_type === 'sms') && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Template <span className="text-red-500">*</span>
                </label>
                <TooltipProvider>
                  {(() => {
                    const templatesHelp = getHelpContent('sequenceTemplates');
                    return templatesHelp ? (
                      <HelpIcon
                        tooltip="Templates must be active to appear here"
                        modalTitle={templatesHelp.title}
                        modalContent={templatesHelp.content}
                        size="sm"
                        variant="subtle"
                      />
                    ) : null;
                  })()}
                </TooltipProvider>
              </div>
              <select
                value={formData.template_id}
                onChange={(e) => setFormData({ ...formData, template_id: e.target.value })}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                  errors.template_id ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Select a template</option>
                {filteredTemplates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
              {errors.template_id && (
                <p className="mt-1 text-sm text-red-600">{errors.template_id}</p>
              )}
              {filteredTemplates.length === 0 && (
                <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-sm text-yellow-800 font-medium mb-1">
                    No active {formData.step_type} templates available
                  </p>
                  <p className="text-sm text-yellow-700">
                    Templates must be set to <strong>"active"</strong> status (not "draft") to appear in the sequence editor. 
                    Go to Templates, edit your template, change the status to "active", and save it.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Delay Fields (for delay step) */}
          {formData.step_type === 'delay' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Delay Days
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.delay_days || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      delay_days: e.target.value ? parseInt(e.target.value, 10) : undefined,
                    })
                  }
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    errors.delay_days ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="0"
                />
                {errors.delay_days && (
                  <p className="mt-1 text-sm text-red-600">{errors.delay_days}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Delay Hours (0-23)
                </label>
                <input
                  type="number"
                  min="0"
                  max="23"
                  value={formData.delay_hours || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      delay_hours: e.target.value ? parseInt(e.target.value, 10) : undefined,
                    })
                  }
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    errors.delay_hours ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="0"
                />
                {errors.delay_hours && (
                  <p className="mt-1 text-sm text-red-600">{errors.delay_hours}</p>
                )}
              </div>
              {errors.delay && (
                <p className="col-span-2 mt-1 text-sm text-red-600">{errors.delay}</p>
              )}
            </div>
          )}

          {/* Conditions (for condition step) */}
          {formData.step_type === 'condition' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Conditions (JSON)
              </label>
              <textarea
                value={JSON.stringify(formData.conditions || {}, null, 2)}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value);
                    setFormData({ ...formData, conditions: parsed });
                  } catch {
                    // Invalid JSON, but allow typing
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                rows={6}
                placeholder='{"field": "value"}'
              />
              <p className="mt-1 text-xs text-gray-500">
                Note: Condition steps are not currently processed by the backend (future feature)
              </p>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700"
            >
              {step ? 'Update Step' : 'Add Step'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

